import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { formatCents, parsePesosToCents } from '@/features/catalog/catalog';
import { listParticipants, type Participant } from '@/features/works/works';
import { createAdjustment } from './closing';

interface Props {
  organizationId: string;
  year: number;
  month: number;
  onSaved: () => void;
  onError: (mensaje: string) => void;
}

type Tipo = 'externo' | 'entre';

/**
 * Alta de un ajuste balanceado (D3).
 *
 * La interfaz nunca arma un asiento de una sola pata: elegís a quién afecta y
 * contra qué se balancea, y la base escribe las dos líneas. La vista previa
 * muestra las dos, para que quede claro que el ajuste no crea ni destruye
 * dinero: lo mueve.
 */
export function AdjustmentForm({ organizationId, year, month, onSaved, onError }: Props) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [tipo, setTipo] = useState<Tipo>('externo');
  const [participante, setParticipante] = useState('');
  const [contraparte, setContraparte] = useState('');
  const [importe, setImporte] = useState('');
  const [concepto, setConcepto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lista = await listParticipants(organizationId);
        if (!cancelado) setParticipants(lista);
      } catch (e) {
        if (!cancelado) onError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
    // `onError` es estable en la práctica; incluirlo re-dispararía la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const cents = parsePesosToCents(importe);
  const nombreDe = (id: string) => participants.find((p) => p.id === id)?.display_name ?? '—';

  const puedeGuardar =
    participante !== '' &&
    cents !== null &&
    cents !== 0 &&
    motivo.trim() !== '' &&
    (tipo === 'externo' || (contraparte !== '' && contraparte !== participante));

  const guardar = async () => {
    if (!puedeGuardar || cents === null) return;
    setGuardando(true);
    try {
      await createAdjustment({
        organizationId,
        year,
        month,
        participantId: participante,
        counterpartParticipantId: tipo === 'entre' ? contraparte : null,
        amountCents: cents,
        concept: concepto.trim() || 'Ajuste',
        reason: motivo.trim(),
      });
      onSaved();
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3"
      data-testid="adjustment-form"
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm">Tipo de ajuste</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="tipo-ajuste"
            checked={tipo === 'externo'}
            onChange={() => setTipo('externo')}
            className="size-4"
            data-testid="adj-type-external"
          />
          Externo / organización
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="tipo-ajuste"
            checked={tipo === 'entre'}
            onChange={() => setTipo('entre')}
            className="size-4"
            data-testid="adj-type-between"
          />
          Entre participantes
        </label>
      </fieldset>

      <label className="flex flex-col gap-1 text-sm">
        A favor de
        <select
          value={participante}
          onChange={(e) => setParticipante(e.target.value)}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
          data-testid="adj-participant"
        >
          <option value="">Elegir…</option>
          {participants.map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name}
            </option>
          ))}
        </select>
      </label>

      {tipo === 'entre' && (
        <label className="flex flex-col gap-1 text-sm">
          A cargo de
          <select
            value={contraparte}
            onChange={(e) => setContraparte(e.target.value)}
            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
            data-testid="adj-counterpart"
          >
            <option value="">Elegir…</option>
            {participants
              .filter((p) => p.id !== participante)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
          </select>
        </label>
      )}

      <label className="flex flex-col gap-1 text-sm">
        Importe
        <input
          inputMode="decimal"
          value={importe}
          onChange={(e) => setImporte(e.target.value)}
          placeholder="0"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
          data-testid="adj-amount"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Concepto
        <input
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          placeholder="Insumos, combustible, reintegro…"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
          data-testid="adj-concept"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Motivo
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por qué se ajusta"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
          data-testid="adj-reason"
        />
        <span className="text-xs text-[var(--tinta-tenue)]">
          Obligatorio: queda en la bitácora.
        </span>
      </label>

      {/* Vista previa de las DOS patas: un ajuste mueve dinero, no lo crea. */}
      {participante !== '' && cents !== null && cents !== 0 && (
        <div className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-[var(--superficie-suave)] p-2 text-sm">
          <span className="text-xs uppercase opacity-60">Asiento</span>
          <span className="flex justify-between">
            <span>{nombreDe(participante)}</span>
            <span className="text-[var(--exito)]">{formatCents(cents)}</span>
          </span>
          <span className="flex justify-between">
            <span>
              {tipo === 'entre'
                ? contraparte
                  ? nombreDe(contraparte)
                  : 'Elegí a cargo de quién'
                : 'Cuenta de la organización'}
            </span>
            <span className="text-[var(--peligro)]">{formatCents(-cents)}</span>
          </span>
          <span className="flex justify-between border-t border-[var(--linea)] pt-1 text-xs text-[var(--tinta-tenue)]">
            <span>Suma</span>
            <span>{formatCents(0)}</span>
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={() => void guardar()}
        disabled={!puedeGuardar || guardando}
        className="min-h-12 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 font-medium text-[var(--sobre-accion)] disabled:opacity-50"
        data-testid="adj-save"
      >
        {guardando ? 'Guardando…' : 'Registrar ajuste'}
      </button>
    </div>
  );
}
